// Privacy-compliant Google Analytics 4 helper
// Every trackEvent call is mirrored to PostHog (also consent-gated).
import { posthogCapture } from "./posthog";

// Hash user ID for privacy
async function hashUserId(userId: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(userId);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Initialize GA4 only when user has consented
export function initializeGA4(measurementId: string, canUseAnalytics: boolean) {
  if (!canUseAnalytics || typeof window === 'undefined') return;

  // Check if already loaded
  if (window.gtag) return;

  // Load GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function(...args: GtagArgs) {
    window.dataLayer.push(args);
  };
  
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    anonymize_ip: true,
    cookie_flags: 'SameSite=None;Secure'
  });

  
}

// Disable GA4 tracking
export function disableGA4(measurementId?: string) {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  // Disable GA tracking for specific or all measurement IDs
  if (measurementId) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Object.defineProperty(window, `ga-disable-${measurementId}`, { value: true });
  }
  
}

// Track events with privacy checks
export async function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>,
  canUseAnalytics?: boolean
) {
  if (!canUseAnalytics || typeof window === 'undefined') {
    return;
  }

  const eventParams = params ? { ...params } : {};

  // Hash user_id if present
  if (eventParams.user_id && typeof eventParams.user_id === 'string') {
    eventParams.user_id = await hashUserId(eventParams.user_id);
  }

  posthogCapture(eventName, eventParams);

  if (!window.gtag) return;
  window.gtag('event', eventName, eventParams);
}

// Predefined event helpers
export async function trackSignup(method: string, userId: string, canUseAnalytics: boolean) {
  await trackEvent('sign_up', {
    method,
    user_id: userId
  }, canUseAnalytics);
}

export async function trackLogin(method: string, userId: string, canUseAnalytics: boolean) {
  await trackEvent('login', {
    method,
    user_id: userId
  }, canUseAnalytics);
}

export async function trackChapterUpload(panelCount: number, userId: string, canUseAnalytics: boolean) {
  await trackEvent('chapter_upload', {
    panel_count: panelCount,
    user_id: userId
  }, canUseAnalytics);
}

export async function trackOCRComplete(
  panelCount: number,
  duration: number,
  userId: string,
  canUseAnalytics: boolean
) {
  await trackEvent('ocr_complete', {
    panel_count: panelCount,
    duration_seconds: Math.round(duration / 1000),
    user_id: userId
  }, canUseAnalytics);
}

export async function trackSpeakerAssignment(
  totalAssignments: number,
  autoAssigned: number,
  avgConfidence: number,
  userId: string,
  canUseAnalytics: boolean
) {
  await trackEvent('speaker_assignment_complete', {
    total_assignments: totalAssignments,
    auto_assigned: autoAssigned,
    avg_confidence: avgConfidence,
    user_id: userId
  }, canUseAnalytics);
}

export async function trackScriptGeneration(
  wordCount: number,
  duration: number,
  userId: string,
  canUseAnalytics: boolean
) {
  await trackEvent('script_generation', {
    word_count: wordCount,
    duration_seconds: Math.round(duration / 1000),
    user_id: userId
  }, canUseAnalytics);
}

export async function trackScriptExport(
  format: string,
  wordCount: number,
  userId: string,
  canUseAnalytics: boolean
) {
  await trackEvent('script_export', {
    format,
    word_count: wordCount,
    user_id: userId
  }, canUseAnalytics);
}

export async function trackCheckoutOpened(
  productType: 'subscription' | 'potion' | 'sonic',
  item: string,
  canUseAnalytics: boolean
) {
  await trackEvent('checkout_opened', {
    product_type: productType,
    item
  }, canUseAnalytics);
}

export async function trackPurchaseSuccess(
  productType: string,
  item: string,
  canUseAnalytics: boolean
) {
  await trackEvent('purchase_success', {
    product_type: productType,
    item
  }, canUseAnalytics);
}

export async function trackError(
  errorType: string,
  errorMessage: string,
  page: string,
  userId: string | null,
  canUseAnalytics: boolean
) {
  await trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage.substring(0, 100),
    page,
    user_id: userId || 'anonymous'
  }, canUseAnalytics);
}

export async function trackProjectCreated(
  contentType: string,
  mediaType: string,
  userId: string,
  canUseAnalytics: boolean
) {
  await trackEvent('project_created', {
    content_type: contentType,
    media_type: mediaType,
    user_id: userId
  }, canUseAnalytics);
}

export async function trackScriptGeneratedWithStyle(
  scriptStyle: string,
  wordCount: number,
  panelCount: number,
  duration: number,
  userId: string,
  canUseAnalytics: boolean
) {
  await trackEvent('script_generated', {
    script_style: scriptStyle,
    word_count: wordCount,
    panel_count: panelCount,
    duration_seconds: Math.round(duration / 1000),
    user_id: userId
  }, canUseAnalytics);
}

export async function trackBlockedSeriesDetected(
  riskLevel: string,
  publisher: string | null,
  userId: string | null,
  canUseAnalytics: boolean
) {
  await trackEvent('blocked_series_detected', {
    risk_level: riskLevel,
    publisher: publisher || 'unknown',
    user_id: userId || 'anonymous'
  }, canUseAnalytics);
}

export async function trackGuidelinesViewed(
  section: string,
  timeOnPage: number,
  userId: string | null,
  canUseAnalytics: boolean
) {
  await trackEvent('guidelines_viewed', {
    section: section,
    time_on_page_seconds: timeOnPage,
    user_id: userId || 'anonymous'
  }, canUseAnalytics);
}

// Type declarations
type GtagArgs = [string, ...unknown[]];

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: GtagArgs) => void;
  }
}
